import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Editor for break-coverage entries on a single spot. Each entry produces a
// virtual spot in the covering photographer's plan, scoped to the start/end
// window. After every mutation we re-fetch the spot from the server so the
// UI matches the truth in the database (this is what makes the "added then
// disappeared" symptom go away when reopening the dialog).
export default function BreakCoverageEditor({ spot, eventPhotographers, onChange, onPersist }) {
  const [coveringId, setCoveringId] = useState('');
  const [coversForId, setCoversForId] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const coverage = spot?.break_coverage || [];
  const nameOf = (id) => eventPhotographers.find((p) => p.id === id)?.name || '—';

  const reloadSpot = async () => {
    if (!spot?.id) return;
    try {
      const r = await axios.get(`${API}/spots/${spot.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      onChange?.(r.data);
    } catch {
      /* swallow — onChange will still have the optimistic value */
    }
  };

  const add = async () => {
    if (!coveringId || !start || !end) {
      toast.error('Pick a covering photographer and start/end time');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/spots/${spot.id}/break-coverage`,
        {
          covering_photographer_id: coveringId,
          covers_for_id: coversForId || null,
          start_time: start,
          end_time: end,
          note: note || null,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setCoveringId(''); setCoversForId(''); setStart(''); setEnd(''); setNote('');
      await reloadSpot();
      onPersist?.();
      toast.success('Coverage added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add coverage');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(
        `${API}/spots/${spot.id}/break-coverage/${id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      await reloadSpot();
      onPersist?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };

  return (
    <div className="border-t pt-4">
      <label className="block text-sm font-medium mb-2">🔁 Break coverage</label>
      <p className="text-xs text-slate-500 mb-2">
        Assign another photographer to cover this spot during someone's break (e.g. lunch, switchover).
        The covering photographer will see this spot in their plan with the coverage time window.
      </p>

      {coverage.length > 0 && (
        <div className="space-y-1 mb-3">
          {coverage.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
              <div className="min-w-0">
                <p className="text-slate-800">
                  <b>{nameOf(c.covering_photographer_id)}</b> covers{' '}
                  {c.covers_for_id ? <>for <b>{nameOf(c.covers_for_id)}</b></> : 'the break'}{' '}
                  · {c.start_time}–{c.end_time}
                </p>
                {c.note && <p className="text-slate-500 mt-0.5">{c.note}</p>}
              </div>
              <button
                type="button"
                onClick={() => remove(c.id)}
                className="text-red-500 hover:text-red-700 px-2 py-0.5 text-xs"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select value={coveringId} onChange={(e) => setCoveringId(e.target.value)} className="border rounded px-2 py-1 text-sm">
          <option value="">— Photographer covering —</option>
          {eventPhotographers.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select value={coversForId} onChange={(e) => setCoversForId(e.target.value)} className="border rounded px-2 py-1 text-sm">
          <option value="">— Covering for (optional) —</option>
          {(spot?.assigned_photographers || []).map((id) => (
            <option key={id} value={id}>{nameOf(id)}</option>
          ))}
        </select>
        <input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="Start" />
        <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="End" />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note (optional)"
          className="border rounded px-2 py-1 text-sm sm:col-span-2"
        />
      </div>
      <button
        type="button"
        disabled={busy || !spot?.id}
        onClick={add}
        className="mt-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded disabled:opacity-50"
      >
        {busy ? 'Adding…' : 'Add coverage'}
      </button>
    </div>
  );
}
